import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import AlliePhoto from '../images/Allie_Portrait_EMOO.png';

const placeholderPhoto = 'https://via.placeholder.com/150';

const SHOW_TUTORS = false; // Easy to toggle on/off

const tutors = [
  {
    name: 'Allie',
    photo: AlliePhoto,
    pronouns: 'she/her',
    subjects: 'Calculus, Algebra, Geometry',
    comment: 'Always ready to help with your math questions!',
  },
  {
    name: 'Saahil',
    photo: placeholderPhoto,
    pronouns: 'he/him',
    subjects: 'Calculus, Algebra, Geometry',
    comment: 'Excited to help you learn!',
  },
  {
    name: 'Emiya',
    photo: placeholderPhoto,
    pronouns: 'he/him',
    subjects: 'Calculus, Algebra, Geometry',
    comment: 'Let’s tackle math together!',
  },
  {
    name: 'Mira',
    photo: placeholderPhoto,
    pronouns: 'she/her',
    subjects: 'Calculus, Algebra, Geometry',
    comment: 'Passionate about math and education!',
  },
  {
    name: 'Aanya',
    photo: placeholderPhoto,
    pronouns: 'she/her',
    subjects: 'Calculus, Algebra, Geometry',
    comment: 'I love making complex subjects easy to understand!',
  },
  {
    name: 'Tiago',
    photo: placeholderPhoto,
    pronouns: 'he/him',
    subjects: 'Calculus, Algebra, Geometry',
    comment: 'Math can be fun and exciting!',
  },
  {
    name: 'Jack',
    photo: placeholderPhoto,
    pronouns: 'he/him',
    subjects: 'Calculus, Algebra, Geometry',
    comment: 'I make math fun and easy to learn!',
  },
  {
    name: 'Eric',
    photo: placeholderPhoto,
    pronouns: 'he/him',
    subjects: 'Calculus, Algebra, Geometry',
    comment: 'I’m here to help you succeed in your studies!',
  },
  {
    name: 'Kai',
    photo: placeholderPhoto,
    pronouns: 'they/them',
    subjects: 'Calculus, Algebra, Geometry',
    comment: 'Math is life!',
  },
 ];

 const PageContainer = styled(motion.div)`
  padding: 4rem 2rem;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  background: #1c2f4f; // Dark blue color
  // Removing the gradient and animation
`;

const ContentWrapper = styled(motion.div)`
 max-width: 1200px;
 margin: 0 auto;
 background-color: rgba(255, 255, 255, 0.9);
 border-radius: 20px;
 box-shadow: 0 10px 30px rgba(28, 47, 79, 0.1);
 padding: 3rem;
 position: relative;
 z-index: 1;

 @media (max-width: 768px) {
   padding: 2rem;
 }
`;

const TitleContainer = styled(motion.div)`
 display: flex;
 justify-content: space-between;
 align-items: center;
 margin-bottom: 2rem;
 flex-wrap: wrap;
 gap: 1rem;

 @media (max-width: 768px) {
   flex-direction: column;
   align-items: flex-start;
 }
`;

const Title = styled(motion.h1)`
 color: #1c2f4f;
 font-size: 2.5rem;
 margin: 0;

 @media (max-width: 768px) {
   font-size: 2rem;
 }
`;

const FilterContainer = styled(motion.div)`
 display: flex;
 align-items: center;
`;

const Dropdown = styled(motion.select)`
 padding: 0.75rem 1rem;
 font-size: 1rem;
 border: 2px solid #1c2f4f;
 border-radius: 50px;
 background-color: #fff;
 color: #1c2f4f;
 cursor: pointer;
 transition: all 0.3s ease;

 &:hover, &:focus {
   background-color: #e6eef8;
   outline: none;
 }
`;

const Grid = styled(motion.div)`
 display: grid;
 grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
 gap: 2rem;
`;

const Card = styled(motion.div)`
 background-color: #fff;
 border-radius: 12px;
 box-shadow: 0 4px 8px rgba(28, 47, 79, 0.1);
 padding: 1.5rem;
 text-align: center;
 transition: transform 0.3s ease, box-shadow 0.3s ease;

 &:hover {
   transform: translateY(-5px);
   box-shadow: 0 6px 12px rgba(28, 47, 79, 0.15);
 }
`;

const Photo = styled(motion.img)`
 border-radius: 50%;
 width: 150px;
 height: 150px;
 object-fit: cover;
 margin-bottom: 1rem;
 border: 4px solid #e6eef8;
 box-shadow: 0 4px 8px rgba(28, 47, 79, 0.1);
`;

const Name = styled(motion.h2)`
 color: #1c2f4f;
 margin-bottom: 0.5rem;
 font-size: 1.5rem;
`;

const InfoContainer = styled(motion.div)`
 text-align: left;
 margin: 0 auto;
 max-width: 100%;
`;

const Info = styled(motion.p)`
 color: #1c2f4f;
 font-size: 0.9rem;
 line-height: 1.6;
 margin-bottom: 0.5rem;
`;

const BoldText = styled.span`
 font-weight: bold;
 color: #1c2f4f;
`;

const Session = styled(motion.div)`
 margin: 0.75rem 0;
 padding: 0.75rem 1rem;
 background-color: #e6eef8;
 color: #1c2f4f;
 border-radius: 8px;
 display: flex;
 justify-content: space-between;
 align-items: center;
 flex-wrap: wrap;
 gap: 0.5rem;

 @media (max-width: 480px) {
   flex-direction: column;
   align-items: flex-start;
 }
`;

const Button = styled(motion.button)`
 background-color: #1c2f4f;
 color: white;
 border: none;
 padding: 0.75rem 1.5rem;
 border-radius: 50px;
 cursor: pointer;
 font-size: 0.9rem;
 font-weight: bold;
 transition: all 0.3s ease;

 &:hover {
   background-color: #2c3e50;
 }
`;

const LoadingMessage = styled.div`
 text-align: center;
 font-size: 1.2rem;
 color: #1c2f4f;
 margin-top: 2rem;
`;

const ErrorMessage = styled.div`
 text-align: center;
 font-size: 1.2rem;
 color: #e74c3c;
 margin-top: 2rem;
`;

const formatTime = (timeString) => {
 if (!timeString) return 'N/A';
 const [hours, minutes] = timeString.split(':');
 let hour = parseInt(hours);
 const ampm = hour >= 12 ? 'PM' : 'AM';
 hour = hour % 12 || 12; // Convert to 12-hour format
 return `${hour}:${minutes.padStart(2, '0')} ${ampm}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString;
  }
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};

const MeetTheTutorsPage = () => {
  const navigate = useNavigate();
  const [selectedDay, setSelectedDay] = useState('');
  const [tutorAvailabilities, setTutorAvailabilities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
 
  const fetchTutorAvailabilities = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/availability/tutors-availability');
      console.log('Fetched tutor availabilities:', response.data);
      setTutorAvailabilities(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching tutor availabilities:', error);
      setError('Failed to fetch tutor availabilities. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    fetchTutorAvailabilities();
  }, []);
 
  const getTutorAvailability = (tutorName) => {
    const tutor = tutorAvailabilities.find(t => t.tutorName === tutorName);
    console.log(`Availability for ${tutorName}:`, tutor);
    return tutor ? tutor.events : [];
  };
 
  const handleDropdownChange = (event) => {
    setSelectedDay(event.target.value);
  };
 
  const getDayOfWeek = (dateString) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const date = new Date(dateString);
    return days[date.getDay()];
  };
 
  const filteredTutors = tutors.filter((tutor) => {
    if (!selectedDay) return true;
    const availability = getTutorAvailability(tutor.name);
    return availability.some(slot => getDayOfWeek(slot.date) === selectedDay);
  });
 
  const openBookingPage = (tutorName, session) => {
    navigate('/book-session', { state: { tutorName, session } });
  };
 
  const handleRemoveEvent = async (tutorName, eventId) => {
    if (!eventId) {
      console.error('Event ID is undefined, cannot remove event');
      return;
    }
  
    try {
      const response = await axios.delete(`/api/availability/${tutorName}/${eventId}`);
  
      if (response.status === 200) {
        fetchTutorAvailabilities(); // Refresh the data
      } else {
        throw new Error('Failed to remove event');
      }
    } catch (error) {
      console.error('Error removing event:', error);
      setMessage({ text: 'Failed to remove event. Please try again.', error: true });
    }
  };  
 
  if (isLoading) {
    return <LoadingMessage>Loading tutor availabilities...</LoadingMessage>;
  }
 
  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  if (!SHOW_TUTORS) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Title>We're Taking a Break!</Title>
          <Info style={{textAlign: 'center', fontSize: '1.2rem', margin: '2rem 0'}}>
            Our tutoring services are temporarily paused as our current leadership team prepares to head off to college. 
            We're working on transitioning to new leadership and appreciate your patience during this time. Be back soon!
          </Info>
        </ContentWrapper>
      </PageContainer>
    );
  }
 
  return (
    <PageContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <ContentWrapper
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <TitleContainer>
          <Title
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            Meet Our Tutors
          </Title>
          <FilterContainer
            initial={{ x: 20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <Dropdown value={selectedDay} onChange={handleDropdownChange}>
              <option value="">Sort By Availability</option>
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                <option key={day} value={day}>
                  {day}
                </option>
              ))}
            </Dropdown>
          </FilterContainer>
        </TitleContainer>
        <Grid>
          <AnimatePresence>
            {filteredTutors.map((tutor, index) => (
              <Card
                key={tutor.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.03 }}
              >
                <Photo
                  src={tutor.photo}
                  alt={tutor.name}
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300 }}
                />
                <Name>{tutor.name}</Name>
                <InfoContainer>
                  <Info><BoldText>Pronouns:</BoldText> {tutor.pronouns}</Info>
                  <Info><BoldText>Subjects:</BoldText> {tutor.subjects}</Info>
                  <Info><BoldText>Comment:</BoldText> {tutor.comment}</Info>
                  <Info><BoldText>Availability:</BoldText></Info>
                  {getTutorAvailability(tutor.name).map((slot, index) => {
                    console.log(`Slot for ${tutor.name}:`, slot);
                    return (
                      <Session
                        key={index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + index * 0.1 }}
                      >
                        <span>
                          {slot.date ? `${formatDate(slot.date)} (${getDayOfWeek(slot.date)})` : 'Date N/A'}
                          {slot.startTime && slot.endTime 
                            ? `, ${formatTime(slot.startTime)} - ${formatTime(slot.endTime)}`
                            : ', Time N/A'}
                        </span>
                        <Button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => openBookingPage(tutor.name, slot)}
                        >
                          Book
                        </Button>
                      </Session>
                    );
                  })}
                </InfoContainer>
              </Card>
            ))}
          </AnimatePresence>
        </Grid>
      </ContentWrapper>
    </PageContainer>
  );
 };
 
 export default MeetTheTutorsPage;